// AlertsTab — shows earned badges, recent sessions, and streak status
import { BADGE_DEFS } from "../../constants/badges";

const TYPE_LABEL = {
  spelling: "Spelling",
  meaning:  "Meaning",
  synonym:  "Synonyms",
  antonym:  "Antonyms",
};
const TYPE_COLOR = {
  spelling: "var(--blue)",
  meaning:  "var(--green)",
  synonym:  "var(--amber)",
  antonym:  "var(--accent)",
};

export function AlertsTab({ user, data }) {
  const streak     = data.streaks?.[user.id] || 0;
  const myPoints   = data.points?.[user.id] || 0;
  const myBadgeIds = new Set((data.badges?.[user.id] || []).map(b => b.id));
  const myBadges   = (data.badges?.[user.id] || [])
    .slice()
    .sort((a, b) => b.earnedAt - a.earnedAt);

  const mySessions = data.sessions
    .filter(s => s.userId === user.id)
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const today = new Date().toDateString();
  const practicedToday = mySessions.some(s => new Date(s.date).toDateString() === today);

  // Next streak badge milestone
  const nextStreakMilestone = [3, 7, 14, 30].find(n => streak < n);

  return (
    <div>
      {/* Streak status */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".75rem", color: "var(--navy)" }}>🔥 Streak Status</div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: streak > 0 ? "var(--amber-light, #fef3c7)" : "var(--surface2)",
            border: streak > 0 ? "3px solid var(--amber, #f59e0b)" : "3px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 800, fontSize: "1.1rem", color: streak > 0 ? "var(--amber, #d97706)" : "var(--muted)" }}>{streak}</span>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: ".9rem" }}>
              {streak === 0 ? "No streak yet" : `${streak} day${streak !== 1 ? "s" : ""} in a row! 🔥`}
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--muted)", marginTop: 2 }}>
              {practicedToday
                ? "✅ You've practised today — streak safe!"
                : streak > 0
                  ? "⚠️ Practice today to keep your streak going!"
                  : "Start practising to build a streak!"}
            </div>
            {nextStreakMilestone && (
              <div style={{ fontSize: ".75rem", color: "var(--blue)", marginTop: 4, fontWeight: 600 }}>
                🎯 {nextStreakMilestone - streak} more day{nextStreakMilestone - streak !== 1 ? "s" : ""} to the {nextStreakMilestone}-day badge!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent sessions */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".75rem", color: "var(--navy)" }}>📋 Recent Activity</div>
        {mySessions.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: ".85rem", textAlign: "center", padding: ".75rem 0" }}>No sessions yet — go practise!</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
            {mySessions.map(s => {
              const typeKey = s.type || "spelling";
              const label = TYPE_LABEL[typeKey] || "Quiz";
              const color = TYPE_COLOR[typeKey] || "var(--blue)";
              const dateStr = new Date(s.date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
              const mins = Math.floor((s.timeSec || 0) / 60);
              const secs = (s.timeSec || 0) % 60;
              return (
                <div key={s.id || s.date} style={{
                  display: "flex", alignItems: "center", gap: ".6rem",
                  background: "var(--surface2)", borderRadius: 10, padding: ".6rem .85rem",
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, fontSize: ".82rem", color }}>{label}</span>
                    <span style={{ fontSize: ".75rem", color: "var(--muted)", marginLeft: ".5rem" }}>{dateStr}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".78rem" }}>
                    <span style={{
                      fontWeight: 700,
                      color: s.score >= 80 ? "var(--green)" : s.score >= 60 ? "var(--amber)" : "var(--red, #ef4444)",
                    }}>{s.score}%</span>
                    <span style={{ color: "var(--muted)" }}>{mins > 0 ? `${mins}m ` : ""}{secs}s</span>
                    {s.ptsEarned > 0 && <span style={{ color: "var(--blue)", fontWeight: 600 }}>+{s.ptsEarned}pts</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Earned badges */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".75rem" }}>
          <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", color: "var(--navy)" }}>🏅 Badges</div>
          <div style={{ fontSize: ".75rem", color: "var(--muted)" }}>{myBadgeIds.size} / {BADGE_DEFS.length} earned</div>
        </div>

        {/* Earned */}
        {myBadges.length > 0 && (
          <>
            <div style={{ fontSize: ".75rem", color: "var(--muted)", marginBottom: ".4rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Earned</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem" }}>
              {myBadges.map(({ id, earnedAt }) => {
                const def = BADGE_DEFS.find(b => b.id === id);
                if (!def) return null;
                return (
                  <div key={id} title={def.desc} style={{
                    display: "flex", alignItems: "center", gap: ".4rem",
                    background: "var(--blue-light, #dbeafe)", border: "1.5px solid var(--blue)",
                    borderRadius: 999, padding: ".3rem .7rem", fontSize: ".78rem",
                  }}>
                    <span>{def.icon}</span>
                    <span style={{ fontWeight: 700, color: "var(--blue)" }}>{def.label}</span>
                    {earnedAt && <span style={{ color: "var(--muted)", fontSize: ".68rem" }}>{new Date(earnedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Locked badges — next 5 to unlock */}
        {(() => {
          const locked = BADGE_DEFS.filter(b => !myBadgeIds.has(b.id)).slice(0, 5);
          if (locked.length === 0) return (
            <div style={{ color: "var(--green)", fontWeight: 700, fontSize: ".85rem", textAlign: "center", padding: ".5rem" }}>
              🎉 You've unlocked every badge!
            </div>
          );
          return (
            <>
              <div style={{ fontSize: ".75rem", color: "var(--muted)", marginBottom: ".4rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Coming up</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
                {locked.map(def => (
                  <div key={def.id} title={def.desc} style={{
                    display: "flex", alignItems: "center", gap: ".4rem",
                    background: "var(--surface2)", border: "1.5px solid var(--border)",
                    borderRadius: 999, padding: ".3rem .7rem", fontSize: ".78rem",
                    opacity: 0.6,
                  }}>
                    <span style={{ filter: "grayscale(1)" }}>{def.icon}</span>
                    <span style={{ color: "var(--muted)" }}>{def.label}</span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Total points summary */}
      <div className="card" style={{ textAlign: "center" }}>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".6rem", color: "var(--navy)" }}>⭐ Points Total</div>
        <div style={{ fontFamily: "var(--heading-font, 'Nunito', sans-serif)", fontWeight: 700, fontSize: "3.25rem", color: "var(--blue)", lineHeight: 1 }}>{myPoints.toLocaleString()}</div>
        <div style={{ fontSize: ".78rem", color: "var(--muted)", marginTop: ".35rem" }}>points earned across all quizzes</div>
      </div>
    </div>
  );
}
